import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, X, Check, FileText, Home, LogOut, Crown, Trash2, Plus, Sparkles, UserCheck, UserPlus, Users as UsersIcon, Fingerprint } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/use-window-size";
import API from "../config";

export default function Game() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const [room, setRoom] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPattern, setShowPattern] = useState(false);
  const [patternData, setPatternData] = useState(null);
  const [revealingPlayerId, setRevealingPlayerId] = useState(null);
  const [showImpostorChoice, setShowImpostorChoice] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);
  const [showVoteConfirm, setShowVoteConfirm] = useState(false);
  const [showVoteSummary, setShowVoteSummary] = useState(false);
  const [voteSummary, setVoteSummary] = useState([]);
  const [isWordVisible, setIsWordVisible] = useState(false);
  const [isPeekingWords, setIsPeekingWords] = useState(false);
  const [showEndGameConfirm, setShowEndGameConfirm] = useState(false);
  const [deleteRevealPlayerId, setDeleteRevealPlayerId] = useState(null);
  const [seenWordsThisRound, setSeenWordsThisRound] = useState(() => new Set());
  const [missionHoldNonce, setMissionHoldNonce] = useState(0);
  const [revealHoldNonce, setRevealHoldNonce] = useState(0);
  const latestFetchNonceRef = useRef(0);

  // Local Mode States
  const [localRevealPlayer, setLocalRevealPlayer] = useState(null);
  const [showLocalRevealModal, setShowLocalRevealModal] = useState(false);
  const [localWordVisible, setLocalWordVisible] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [roster, setRoster] = useState([]);
  const [selectedRosterNames, setSelectedRosterNames] = useState([]);
  const [newRosterName, setNewRosterName] = useState("");
  const [error, setError] = useState(null);
  const [directorPhase, setDirectorPhase] = useState("select"); // 'select', 'input', 'ready'
  const [directorPlayerId, setDirectorPlayerId] = useState(null);
  const [directorWords, setDirectorWords] = useState({ agent: "", infiltrator: "" });
  const [showDirectorModal, setShowDirectorModal] = useState(false);

  // Card Reveal States
  const [isWordRevealed, setIsWordRevealed] = useState(false);
  const [isMissionRevealed, setIsMissionRevealed] = useState(false);
  const revealTimerRef = useRef(null);
  const missionTimerRef = useRef(null);
  const missionAutoHideRef = useRef(null);
  const revealStartPosRef = useRef({ x: 0, y: 0 });
  const revealingPlayerIdRef = useRef(null);
  const revealHoldSessionRef = useRef(0);
  const missionHoldSessionRef = useRef(0);
  const activeRevealPointerRef = useRef(null);
  const deleteSwipeStartXRef = useRef(0);
  const deleteSwipeActiveIdRef = useRef(null);
  const deleteSwipeTouchActiveIdRef = useRef(null);

  useEffect(() => {
    // New round: restore from storage (or start empty).
    const round = room?.current_round;
    if (!round && round !== 0) return;
    try {
      const raw = localStorage.getItem(`room_${roomCode}_seen_words_round_${round}`);
      if (!raw) {
        setSeenWordsThisRound(new Set());
        return;
      }
      const arr = JSON.parse(raw);
      setSeenWordsThisRound(new Set(Array.isArray(arr) ? arr : []));
    } catch {
      setSeenWordsThisRound(new Set());
    }
  }, [room?.current_round]);

  useEffect(() => {
    // Persist within a round so refresh doesn't force re-reveal.
    const round = room?.current_round;
    if (!round && round !== 0) return;
    try {
      localStorage.setItem(
        `room_${roomCode}_seen_words_round_${round}`,
        JSON.stringify(Array.from(seenWordsThisRound))
      );
    } catch {
      // ignore storage failures
    }
  }, [seenWordsThisRound, room?.current_round, roomCode]);

  const endRevealHold = useCallback(() => {
    // Invalidate any pending reveal timers.
    revealHoldSessionRef.current += 1;
    revealingPlayerIdRef.current = null;
    activeRevealPointerRef.current = null;
    clearTimeout(revealTimerRef.current);
    setRevealingPlayerId(null);
    setIsWordRevealed(false);
  }, []);

  useEffect(() => {
    revealingPlayerIdRef.current = revealingPlayerId;
  }, [revealingPlayerId]);

  // If the press ends outside the button (mobile browser quirks, focus changes, etc.),
  // make sure the reveal state is always reset.
  useEffect(() => {
    if (!revealingPlayerId) return;

    const onEnd = () => endRevealHold();

    // Use capture so we run even if an inner element stops propagation.
    // Mark touch listeners non-passive so preventDefault can work elsewhere.
    window.addEventListener("pointerup", onEnd, true);
    window.addEventListener("pointercancel", onEnd, true);
    window.addEventListener("mouseup", onEnd, true);
    window.addEventListener("touchend", onEnd, { capture: true, passive: false });
    window.addEventListener("touchcancel", onEnd, { capture: true, passive: false });
    window.addEventListener("blur", onEnd);
    document.addEventListener("visibilitychange", onEnd);

    return () => {
      window.removeEventListener("pointerup", onEnd, true);
      window.removeEventListener("pointercancel", onEnd, true);
      window.removeEventListener("mouseup", onEnd, true);
      window.removeEventListener("touchend", onEnd, { capture: true });
      window.removeEventListener("touchcancel", onEnd, { capture: true });
      window.removeEventListener("blur", onEnd);
      document.removeEventListener("visibilitychange", onEnd);
    };
  }, [revealingPlayerId, endRevealHold]);

  // If user taps elsewhere, close any revealed delete action.
  useEffect(() => {
    if (!deleteRevealPlayerId) return;
    const onDown = (e) => {
      // Don't auto-close when interacting with the delete control itself.
      const t = e?.target;
      if (t && typeof t.closest === "function" && t.closest('[data-delete-reveal="true"]')) return;
      setDeleteRevealPlayerId(null);
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, [deleteRevealPlayerId]);

  useEffect(() => {
    return () => {
      clearTimeout(revealTimerRef.current);
      clearTimeout(missionTimerRef.current);
      clearTimeout(missionAutoHideRef.current);
    };
  }, []);

  // Refs para acceso a estado fresco dentro de intervalos
  // Refs para acceso a estado fresco dentro de intervalos
  const isLocalRef = useRef(false);



  const fetchRoom = useCallback(async (id) => {
    const fetchNonce = ++latestFetchNonceRef.current;
    try {
      const response = await axios.get(`${API}/rooms/${roomCode}`, {
        params: { player_id: id }
      });

      // Avoid out-of-order responses overwriting newer state (common with rapid actions like kicking two players).
      if (fetchNonce !== latestFetchNonceRef.current) return;

      setRoom(response.data);
      setError(null);

      const player = response.data.players.find(p => p.id === id);

      // FIX: Si estamos en modo local y el jugador actual ha desaparecido (fue borrado y purgado entre rondas),
      // adoptamos automáticamente la identidad del host actual para no quedar huérfanos.
      if (!player && response.data.settings?.is_local) {
        const newHostId = response.data.host_id;
        if (newHostId && newHostId !== id) {
          console.log("Jugador local huérfano. Cambiando identidad al Host:", newHostId);
          localStorage.setItem(`room_${roomCode}`, newHostId);
          setPlayerId(newHostId);
          // Reintentamos fetch inmediatamente con la nueva ID
          fetchRoom(newHostId);
          return;
        }
      }

      setCurrentPlayer(player);

      // Actualizar Refs para uso en intervalos/efectos sin dependencias
      if (response.data) {
        isLocalRef.current = response.data.settings?.is_local || false;
      }

      if (player) {
        setHasVoted(player.has_voted || false);
      }

      if (response.data.status === "lobby" && response.data.current_round === 0) {
        navigate(`/lobby/${roomCode}`);
      }

      if (response.data.status === "ended") {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      console.error("Error fetching room:", error);
      if (error.response?.status === 404) {
        toast.error("La sala ya no existe");
        localStorage.removeItem(`room_${roomCode}`);
        navigate('/');
      } else {
        // Para otros errores (500, red, etc), no cerramos la sesión, solo mostramos el error
        setError("Error de conexión con el servidor. Reintentando...");
      }
    } finally {
      setLoading(false);
    }
  }, [roomCode, navigate]);

  useEffect(() => {
    const id = localStorage.getItem(`room_${roomCode}`);
    if (!id) {
      toast.error("No tienes acceso a esta sala");
      navigate("/");
      return;
    }
    setPlayerId(id);
    fetchRoom(id);
    const interval = setInterval(() => fetchRoom(id), 2000);

    const activityInterval = setInterval(async () => {
      // OPTIMIZACIÓN: En local mode no enviamos heartbeat para no saturar 
      // la base de datos con escrituras innecesarias.
      if (isLocalRef.current) return;

      try {
        await axios.post(`${API}/rooms/${roomCode}/update-activity`, null, {
          params: { player_id: id }
        });
      } catch (error) {
        console.error("Error updating activity:", error);
      }
    }, 5000);

    const handleBeforeUnload = (e) => {
      // Small attempt to notify the server on close/refresh
      axios.post(`${API}/rooms/${roomCode}/update-activity`, null, {
        params: { player_id: id, disconnection_type: 'closing' }
      }).catch(() => { });
    };

    const handleFocus = () => {
      // Force immediate fetch and heartbeat on regain focus (mobile coming back from sleep)
      fetchRoom(id);
      axios.post(`${API}/rooms/${roomCode}/update-activity`, null, {
        params: { player_id: id }
      }).catch(() => { });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("focus", handleFocus);

    // PERSISTENCIA: Guardar esta sala como activa para el usuario (CON METADATOS)
    if (room && roomCode) {
      const activeRoomsList = JSON.parse(localStorage.getItem('active_rooms_list') || '[]');
      const existingIndex = activeRoomsList.findIndex(r => r.code === roomCode);

      const newRoomData = {
        code: roomCode,
        timestamp: Date.now(),
        isLocal: room.settings?.is_local || false,
        playerCount: room.players ? room.players.length : 0
      };

      let updatedList;
      if (existingIndex === -1) {
        updatedList = [newRoomData, ...activeRoomsList].slice(0, 5);
      } else {
        // Actualizar datos existentes y mover al principio
        const otherRooms = activeRoomsList.filter(r => r.code !== roomCode);
        updatedList = [newRoomData, ...otherRooms].slice(0, 5);
      }

      localStorage.setItem('active_rooms_list', JSON.stringify(updatedList));
    }

    return () => {
      clearInterval(interval);
      clearInterval(activityInterval);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("focus", handleFocus);
    };
  }, [roomCode, navigate, fetchRoom, room]); // Añadido 'room' a dependencias

  const handleVote = async (targetId, confirmed = false) => {
    if (hasVoted && confirmed) {
      toast.error("Ya has votado");
      return;
    }

    if (room?.settings?.confirm_vote && !confirmed) {
      setSelectedVote(targetId);
      setShowVoteConfirm(true);
      return;
    }

    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/vote`, {
        voter_id: playerId,
        target_id: targetId,
        confirmed: true
      });

      setHasVoted(true);
      setSelectedVote(null);
      setShowVoteConfirm(false);
      toast.success("Voto registrado");

      if (response.data.vote_summary && room?.settings?.vote_type === "public") {
        setVoteSummary(response.data.vote_summary);
        setShowVoteSummary(true);
      }
    } catch (error) {
      toast.error("Error al votar");
    }
  };
  const handleLeaveRoom = async () => {
    if (!confirm("¿Estás seguro de que quieres abandonar la partida?")) return;

    try {
      await axios.post(`${API}/rooms/${roomCode}/leave`, null, {
        params: { player_id: playerId },
      });
      localStorage.removeItem(`room_${roomCode}`);
      toast.success("Has abandonado la sala");
      navigate("/");
    } catch (error) {
      toast.error("Error al abandonar la sala");
    }
  };

  const handleNextRound = async () => {
    // Check if Director Mode is active
    if (room.settings?.director_mode) {
      // Reset director state and show modal
      setDirectorPhase("select");
      setDirectorPlayerId(null);
      setDirectorWords({ agent: "", infiltrator: "" });
      setShowDirectorModal(true);
      return;
    }

    // Normal Flow
    try {
      await axios.post(`${API}/rooms/${roomCode}/start`, {});
      // Force immediate re-fetch
      fetchRoom(playerId);
      toast.success("Nueva ronda iniciada");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error al avanzar ronda");
    }
  };

  // Auto-open Director Modal in Round 0
  useEffect(() => {
    if (room && room.settings?.director_mode && room.current_round === 0 && !showDirectorModal) {
      setDirectorPhase("select");
      setDirectorPlayerId(null);
      setDirectorWords({ agent: "", infiltrator: "" });
      setShowDirectorModal(true);
    }
  }, [room?.current_round, room?.settings?.director_mode]);

  const handleDirectorStartRound = async () => {
    if (!directorPlayerId || !directorWords.agent || !directorWords.infiltrator) {
      toast.error("Faltan datos para iniciar");
      return;
    }

    try {
      await axios.post(`${API}/rooms/${roomCode}/start`, {
        director_player_id: directorPlayerId,
        custom_words: directorWords
      });
      setShowDirectorModal(false);
      fetchRoom(playerId);
      toast.success("Ronda de Director iniciada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar ronda");
    }
  };

  const handleDeleteRoom = async () => {
    if (!isHost) return;

    // Close modal immediately for better UX
    setShowEndGameConfirm(false);

    try {
      const requesterId = room.settings?.is_local ? room.host_id : playerId;
      await axios.delete(`${API}/rooms/${roomCode}`, {
        params: { player_id: requesterId }
      });
      toast.success("Sala eliminada con éxito");
      localStorage.removeItem(`room_${roomCode}`);
      navigate("/");
    } catch (error) {
      toast.error("Error al eliminar la sala.");
      // Re-open if failed, so they can try again
      setShowEndGameConfirm(true);
    }
  };

  const handleManualExpulsion = async (targetId) => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/manual-expulsion`, {
        player_id: targetId
      });
      toast.success("Eliminado manualmente");
    } catch (error) {
      toast.error("Error al registrar eliminación");
    }
  };

  const handleKickPlayer = async (targetId) => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/kick`, {
        player_id: targetId
      });
      toast.success("Jugador eliminado");
      fetchRoom(playerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar jugador");
    }
  };

  const handleShowPattern = async () => {
    if (!currentPlayer.is_alive) {
      try {
        const response = await axios.get(`${API}/rooms/${roomCode}/pattern`);
        setPatternData(response.data);
        setShowPattern(true);

        if (currentPlayer?.role === "impostor") {
          setShowImpostorChoice(true);
        }
      } catch (error) {
        toast.error("Error al obtener el patrón");
      }
    }
  };

  const handleImpostorGuess = async (knewWord) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/impostor-guess`, {
        player_id: playerId,
        knew_word: knewWord
      });

      setShowImpostorChoice(false);
      setShowPattern(false);

      if (response.data.winner) {
        toast.success(knewWord ? "¡Acertaste! Agentes ganan" : "No acertaste");
      } else if (response.data.continue_game) {
        toast.success("La partida continúa...");
      }
    } catch (error) {
      toast.error("Error al procesar respuesta");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoster = async () => {
    const hostId = localStorage.getItem('local_host_id');
    if (!hostId) return;
    try {
      const res = await axios.get(`${API}/roster/${hostId}`);
      setRoster(res.data.names || []);
    } catch (err) {
      console.error("Error fetching roster", err);
    }
  };

  const handleAddNameToRoster = async () => {
    const name = newRosterName.trim();
    if (!name) return;
    const hostId = localStorage.getItem('local_host_id');
    if (!hostId) return;

    if (roster.includes(name)) {
      toast.error("Este nombre ya existe en tu lista");
      return;
    }

    try {
      await axios.post(`${API}/roster/${hostId}`, { name });
      setRoster([...roster, name]);
      setNewRosterName("");
      // Auto-seleccionar
      setSelectedRosterNames(prev => [...prev, name]);
      toast.success("Añadido al roster");
    } catch (err) {
      toast.error("Error al guardar en roster");
    }
  };

  const handleConfirmAddPlayers = async () => {
    if (selectedRosterNames.length === 0) return;

    setLoading(true);
    try {
      for (const name of selectedRosterNames) {
        await axios.post(`${API}/rooms/${roomCode}/add-local-player`, { name });
      }
      toast.success(selectedRosterNames.length === 1 ? "Jugador añadido" : "Jugadores añadidos");
      setShowAddPlayerModal(false);
      setSelectedRosterNames([]);
      fetchRoom(playerId);
    } catch (error) {
      toast.error("Error al añadir jugadores");
    } finally {
      setLoading(false);
    }
  };


  if (loading || !room || !currentPlayer) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines">
        <div className="text-center">
          <div className="font-heading text-2xl text-[hsl(var(--cyber-yellow))] glow-pulse">
            CARGANDO PARTIDA...
          </div>
        </div>
      </div>
    );
  }

  const isHost = (room.settings?.is_local) || (room.host_id === playerId);

  if (currentPlayer.waiting_next_round) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4">
        <div className="text-center glass rounded-lg p-8 md:p-12 space-y-6 max-w-lg">
          <h2 className="font-heading text-2xl text-[hsl(var(--cyber-yellow))]">Partida en Curso</h2>
          <p className="text-[hsl(var(--text-secondary))]">
            Te has unido a una partida que ya ha empezado. Podrás jugar en la siguiente ronda.
          </p>
          <p className="text-sm text-[hsl(var(--text-muted))]">
            Mientras tanto, puedes observar el estado de la partida.
          </p>
          <Button
            onClick={handleLeaveRoom}
            className="bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90 font-heading font-semibold"
          >
            <Home className="w-4 h-4 mr-2" />
            Salir y volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const getRoleColor = (role) => {
    if (role === "agent") return "hsl(var(--cyber-yellow))";
    if (role === "infiltrator") return "hsl(var(--hot-pink))";
    if (role === "impostor") return "hsl(var(--electric-cyan))";
    if (role === "director") return "#a855f7"; // Purple-500
    return "hsl(var(--text-muted))";
  };

  const getRoleLabel = (role) => {
    if (role === "agent") return "AGENTES";
    if (role === "infiltrator") return "INFILTRADOS";
    if (role === "impostor") return "IMPOSTOR";
    if (role === "director") return "DIRECTOR";
    return "DESCONOCIDO";
  };

  const getWinnerInfo = () => {
    if (!room.winning_roles || room.winning_roles.length === 0) {
      return null;
    }

    const team = room.winning_roles.map(getRoleLabel).join(' Y ');
    const color = getRoleColor(room.winning_roles[0]); // Use the color of the first winning role

    return { team, color };
  };

  const winner = room.status === "ended" ? getWinnerInfo() : null;
  const canStartNextRound = (room.settings?.game_mode === "infinite") || (room.settings?.game_mode === "rounds" && room.current_round < room.settings.max_rounds);

  return (
    <div className="min-h-screen scanlines p-4 md:p-8" data-testid="game-page">
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        {!room.settings?.is_local && (
          <Button
            onClick={handleLeaveRoom}
            size="sm"
            variant="ghost"
            className="text-xs text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--hot-pink))]/20 hover:text-[hsl(var(--hot-pink))]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abandonar Partida
          </Button>
        )}
      </div>
      {showConfetti && winner && <Confetti width={width} height={height} recycle={false} numberOfPieces={500} colors={[winner.color, '#FFFFFF']} />}

      {room.status === "ended" && winner && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex flex-col p-3 sm:p-4 lg:p-6" data-testid="game-over-screen">
          {/* Header Section */}
          <div className="shrink-0 text-center space-y-1 sm:space-y-2 mb-2 sm:mb-4">
            <div
              className="font-display text-3xl sm:text-4xl md:text-6xl font-black leading-none"
              style={{ color: winner.color, filter: `drop-shadow(0 0 40px ${winner.color})` }}
            >
              {winner.team}
            </div>
            <div className="font-heading text-lg sm:text-xl md:text-2xl text-[hsl(var(--text-primary))]">
              HAN GANADO
            </div>
          </div>

          {/* Main Content Grid - Grows to fill space with internal scrolling */}
          <div className={`flex-1 min-h-0 w-full max-w-7xl mx-auto grid gap-3 sm:gap-4 ${room.vote_history?.length > 0 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-4xl'}`}>

            {/* Scores Panel */}
            <div className="glass rounded-lg p-3 sm:p-4 flex flex-col min-h-0 shadow-lg border-[hsl(var(--smoke))]">
              <h3 className="shrink-0 font-heading text-base sm:text-lg font-semibold mb-2 text-center">PUNTUACIONES</h3>
              <div className="flex-1 overflow-auto rounded-lg border border-[hsl(var(--smoke))]">
                <table className="w-full text-xs sm:text-sm text-left">
                  <thead className="bg-[hsl(var(--charcoal))] font-heading text-[10px] sm:text-xs uppercase text-[hsl(var(--text-muted))] sticky top-0 z-10">
                    <tr>
                      <th scope="col" className="px-3 py-2 bg-[hsl(var(--charcoal))]">Jugador</th>
                      <th scope="col" className="px-3 py-2 bg-[hsl(var(--charcoal))]">Rol</th>
                      <th scope="col" className="px-3 py-2 bg-[hsl(var(--charcoal))]">Estado</th>
                      {room.settings?.enable_scoring !== false && <th scope="col" className="px-3 py-2 bg-[hsl(var(--charcoal))] text-right">Puntos</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {[...room.players].sort((a, b) => b.points - a.points).map((player, index) => {
                      const isWinner = room.winning_roles?.includes(player.role);

                      let statusBadge;
                      if (!player.is_connected) {
                        statusBadge = <Badge variant="outline" className="text-[10px]" style={{ color: 'hsl(var(--text-muted))', borderColor: 'hsl(var(--text-muted))' }}>OFF</Badge>;
                      } else if (!player.is_alive) {
                        statusBadge = <Badge variant="outline" className="text-[10px]" style={{ color: 'hsl(var(--hot-pink))', borderColor: 'hsl(var(--hot-pink))' }}>ELIM</Badge>;
                      } else {
                        statusBadge = <Badge variant="outline" className="text-[10px]" style={{ color: 'hsl(var(--laser-green))', borderColor: 'hsl(var(--laser-green))' }}>VIVO</Badge>;
                      }

                      return (
                        <tr key={player.id} className="border-b border-[hsl(var(--smoke))] bg-black/20 hover:bg-white/5" data-testid={`score-player-${index}`}>
                          <th scope="row" className="px-3 py-1.5 font-medium whitespace-nowrap text-white">
                            {player.name}
                          </th>
                          <td className="px-3 py-1.5">
                            {player.role && (
                              <Badge
                                variant="outline"
                                className="text-[10px]"
                                style={{
                                  backgroundColor: `${getRoleColor(player.role)}20`,
                                  color: getRoleColor(player.role),
                                  borderColor: `${getRoleColor(player.role)}40`,
                                }}
                              >
                                {getRoleLabel(player.role)}
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            {statusBadge}
                          </td>
                          {room.settings?.enable_scoring !== false && (
                            <td className="px-3 py-1.5 text-right">
                              <Badge className="bg-[hsl(var(--cyber-yellow))]/20 text-[hsl(var(--cyber-yellow))] text-[10px]">
                                {player.points}
                                {isWinner && (
                                  <span className="ml-1 text-green-400 font-bold">
                                    (+{player.role?.toLowerCase() === 'infiltrator' ? '2' : player.role?.toLowerCase() === 'impostor' ? '3' : '1'})
                                  </span>
                                )}
                              </Badge>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Vote History Panel */}
            {room.vote_history && room.vote_history.length > 0 && (
              <div className="glass rounded-lg p-3 sm:p-4 flex flex-col min-h-0 shadow-lg border-[hsl(var(--smoke))]">
                <h3 className="shrink-0 font-heading text-base sm:text-lg font-semibold mb-2 text-center">HISTORIAL</h3>
                <div className="flex-1 overflow-auto rounded-lg border border-[hsl(var(--smoke))]">
                  <table className="w-full text-xs sm:text-sm text-left">
                    <thead className="bg-[hsl(var(--charcoal))] font-heading text-[10px] sm:text-xs uppercase text-[hsl(var(--text-muted))] sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 bg-[hsl(var(--charcoal))] w-16 text-center">Ronda</th>
                        <th className="px-3 py-2 bg-[hsl(var(--charcoal))] w-1/4">Eliminado</th>
                        <th className="px-3 py-2 bg-[hsl(var(--charcoal))]">Votaciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[hsl(var(--smoke))]/30">
                      {[...room.vote_history].reverse().map((record, index) => (
                        <tr key={index} className="bg-black/20 hover:bg-white/5">
                          <td className="px-3 py-2 text-center font-heading text-[hsl(var(--text-muted))]">
                            {record.round}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            {record.expelled_name ? (
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-[hsl(var(--hot-pink))] whitespace-nowrap">{record.expelled_name}</span>
                                {record.expelled_role && (
                                  <Badge variant="outline" className="text-[10px] border-[hsl(var(--smoke))] whitespace-nowrap">
                                    {getRoleLabel(record.expelled_role)}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-[hsl(var(--text-muted))] italic">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {record.manual ? (
                              <span className="text-[hsl(var(--text-muted))] italic">Eliminación Manual</span>
                            ) : (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {record.votes && record.votes.map((vote, vIndex) => (
                                  <div key={vIndex} className="text-xs flex items-center gap-1.5 bg-black/30 px-1.5 py-0.5 rounded border border-[hsl(var(--smoke))]/20">
                                    <span className="text-[hsl(var(--text-secondary))]">{vote.voter}</span>
                                    <span className="text-[hsl(var(--text-muted))] text-[10px]">➜</span>
                                    <span className="text-[hsl(var(--hot-pink))]">{vote.target}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions - Always visible at bottom */}
          <div className="shrink-0 flex flex-wrap items-center justify-center gap-2 sm:gap-4 mt-2 sm:mt-4 pt-2 sm:pt-4 border-t border-[hsl(var(--smoke))]/30 bg-black/40 -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6 pb-safe">
            {/* --- Botón principal de acción (visible para TODOS) --- */}
            {canStartNextRound ? (
              <Button
                data-testid="next-round-button"
                onClick={handleNextRound}
                className="bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold px-4 sm:px-6 py-3 sm:py-4 h-auto text-xs sm:text-sm shadow-[0_0_15px_rgba(255,220,90,0.3)] animate-pulse-slow"
              >
                {room.settings?.game_mode === "infinite"
                  ? "SIGUIENTE RONDA"
                  : `SIGUIENTE (${room.current_round + 1}/${room.settings.max_rounds})`
                }
              </Button>
            ) : room.current_round === 0 && room.settings?.director_mode ? (
              // Fallback button for Director Mode Round 0
              <Button
                onClick={() => setShowDirectorModal(true)}
                className="bg-[hsl(var(--electric-cyan))] text-black hover:bg-[hsl(var(--electric-cyan))]/90 font-heading font-semibold px-4 sm:px-6 py-3 sm:py-4 h-auto text-xs sm:text-sm shadow-[0_0_15px_rgba(0,255,255,0.3)] animate-pulse"
              >
                ELEGIR DIRECTOR (INICIAR)
              </Button>
            ) : (
              <Button
                data-testid="back-to-home-button"
                onClick={() => navigate("/")}
                className="bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold px-4 sm:px-6 py-3 sm:py-4 h-auto text-xs sm:text-sm"
              >
                VOLVER AL INICIO
              </Button>
            )}

            {/* --- Botones solo para el Host --- */}
            {isHost && (
              <Button
                data-testid="end-game-button"
                onClick={() => setShowEndGameConfirm(true)}
                variant="outline"
                className="border-[hsl(var(--hot-pink))] text-[hsl(var(--hot-pink))] hover:bg-[hsl(var(--hot-pink))]/10 hover:text-[hsl(var(--hot-pink))] font-heading font-semibold h-auto py-2 sm:py-3 text-xs sm:text-sm"
              >
                Finalizar
              </Button>
            )}

            {/* --- Botón para abandonar (visible para TODOS) --- */}
            <Button
              onClick={handleLeaveRoom}
              variant="ghost"
              className="text-[hsl(var(--text-muted))] hover:bg-white/10 hover:text-white font-heading font-semibold h-auto py-2 sm:py-3 text-xs sm:text-sm"
            >
              <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Salir
            </Button>
          </div>
        </div>
      )}

      {room.settings?.is_local ? (
        /* --- LOCAL MODE UI --- */
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="text-center">
            <div className="glass border-[hsl(var(--electric-cyan))]/20 rounded-2xl p-4 sm:p-6 mb-2">
              <div className="flex flex-col items-center gap-3">
                <div className="space-y-1">
                  <h2 className="font-display text-4xl sm:text-5xl font-black text-[hsl(var(--electric-cyan))] tracking-tighter drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">
                    MODO LOCAL
                  </h2>
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="flex flex-wrap justify-center items-center gap-2">
                      <Badge variant="outline" className="bg-black/40 text-[hsl(var(--cyber-yellow))] border-[hsl(var(--cyber-yellow))] font-black tracking-widest px-4 py-1">
                        RONDA {room.current_round}
                      </Badge>

                      {room.settings?.starting_player_assistance && room.starting_player_id && (
                        <div className="bg-[hsl(var(--cyber-yellow))]/10 border border-[hsl(var(--cyber-yellow))]/30 rounded-full px-3 py-1.5 flex items-center gap-2">
                          <Sparkles className="w-3 h-3 text-[hsl(var(--cyber-yellow))]" />
                          <span className="text-[10px] font-black text-white tracking-widest uppercase">
                            EMPIEZA: <span className="text-[hsl(var(--cyber-yellow))]">{room.players.find(p => p.id === room.starting_player_id)?.name}</span>
                          </span>
                        </div>
                      )}
                    </div>
                    {(() => {
                      // If Round 0 (Director Mode pre-start), use settings for counts
                      if (room.current_round === 0 && room.settings) {
                        const totalPlayers = room.players.filter(p => p.is_connected).length;
                        const infCount = room.settings.infiltrators_count || 0;
                        const impCount = room.settings.impostor_count || 0;
                        const directorCount = room.settings.director_mode ? 1 : 0;
                        const agentCount = Math.max(0, totalPlayers - infCount - impCount - directorCount);

                        return (
                          <div className="flex gap-2 text-[10px] font-bold tracking-wider uppercase opacity-80">
                            <span className="text-[hsl(var(--cyber-yellow))]">{agentCount} AGENTES</span>
                            <span className="text-[hsl(var(--smoke))]">•</span>
                            <span className="text-[hsl(var(--hot-pink))]">{infCount} INFILTRADOS</span>
                            <span className="text-[hsl(var(--smoke))]">•</span>
                            <span className="text-[hsl(var(--electric-cyan))]">{impCount} IMPOSTORES</span>
                          </div>
                        );
                      }

                      // Otherwise count actual assigned roles
                      const counts = room.players.reduce((acc, p) => {
                        if (p.is_connected && p.role) {
                          acc[p.role] = (acc[p.role] || 0) + 1;
                        }
                        return acc;
                      }, {});
                      return (
                        <div className="flex gap-2 text-[10px] font-bold tracking-wider uppercase opacity-80">
                          <span className="text-[hsl(var(--cyber-yellow))]">{counts.agent || 0} AGENTES</span>
                          <span className="text-[hsl(var(--smoke))]">•</span>
                          <span className="text-[hsl(var(--hot-pink))]">{counts.infiltrator || 0} INFILTRADOS</span>
                          <span className="text-[hsl(var(--smoke))]">•</span>
                          <span className="text-[hsl(var(--electric-cyan))]">{counts.impostor || 0} IMPOSTORES</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  {room.current_round > 0 && (
                    (() => {
                      const participants = room.players.filter(p => p.is_connected && !p.waiting_next_round);
                      const canReveal = participants.length > 0 && participants.every(p => seenWordsThisRound.has(p.id));

                      return (
                        <div
                          className={`glass flex items-center gap-0 p-0 rounded-2xl transition-all relative overflow-hidden h-16 min-w-[280px] border ${canReveal ? "border-[hsl(var(--hot-pink))]/30" : "border-white/10 opacity-70"}`}
                        >
                          {/* Progress Bar Background */}
                          {isPeekingWords && !isMissionRevealed && (
                            <div key={`mission-fill-${missionHoldNonce}`} className="absolute inset-0 bg-white/5 z-0">
                              <div className="h-full bg-white/10 progress-fill-reveal" />
                            </div>
                          )}

                          {/* Text Area */}
                          <div className="flex-1 h-full flex flex-col justify-center items-center px-6 min-w-0 relative z-10 text-center">
                            {isPeekingWords && isMissionRevealed ? (
                              <div className="flex items-center justify-center gap-6 w-full">
                                <div className="flex flex-col items-start leading-none min-w-0">
                                  <span className="text-[9px] uppercase opacity-60 font-black tracking-widest text-white/70">Agente</span>
                                  <span className="text-lg tracking-tighter font-black text-white truncate">
                                    {room.agent_word.toUpperCase()}
                                  </span>
                                </div>
                                <div className="w-px h-8 bg-white/10 self-center" />
                                <div className="flex flex-col items-start leading-none min-w-0">
                                  <span className="text-[9px] uppercase opacity-60 font-black tracking-widest text-white/70">Infiltrado</span>
                                  <span className="text-lg tracking-tighter font-black text-white truncate">
                                    {room.infiltrator_word.toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-heading font-black tracking-wider text-[hsl(var(--hot-pink))]">VER PALABRA AGENTE/INFILTRADO</span>
                                </div>
                                <span className="text-[9px] opacity-60 font-black tracking-[0.2em] italic">
                                  {canReveal ? "MANTÉN PULSADO" : "TODOS DEBEN VER SU PALABRA"}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Fingerprint Hold Button */}
                          <Button
                            variant="ghost"
                            disabled={!canReveal}
                            className="h-full w-20 shrink-0 rounded-none border-l border-white/5 radial-hold-container p-0 flex items-center justify-center transition-all active:scale-95 z-20 touch-none focus:ring-0 focus-visible:ring-0 outline-none hover:bg-transparent active:bg-transparent disabled:opacity-40"
                            style={{ WebkitTouchCallout: "none" }}
                            onPointerDown={() => {
                              if (!canReveal) return;
                              // Always restart mission hold progress from 0.
                              missionHoldSessionRef.current += 1;
                              const session = missionHoldSessionRef.current;
                              setMissionHoldNonce(n => n + 1);
                              clearTimeout(missionAutoHideRef.current);
                              setIsPeekingWords(true);
                              setIsMissionRevealed(false);
                              clearTimeout(missionTimerRef.current);
                              missionTimerRef.current = setTimeout(() => {
                                if (missionHoldSessionRef.current !== session) return;
                                setIsMissionRevealed(true);
                              }, 1500);
                            }}
                            onTouchStart={(e) => {
                              if (!canReveal) return;
                              // Mobile Safari can be flaky with pointer events; use touch as a reliable trigger.
                              e.preventDefault();
                              missionHoldSessionRef.current += 1;
                              const session = missionHoldSessionRef.current;
                              setMissionHoldNonce(n => n + 1);
                              clearTimeout(missionAutoHideRef.current);
                              setIsPeekingWords(true);
                              setIsMissionRevealed(false);
                              clearTimeout(missionTimerRef.current);
                              missionTimerRef.current = setTimeout(() => {
                                if (missionHoldSessionRef.current !== session) return;
                                setIsMissionRevealed(true);
                              }, 1500);
                            }}
                            onPointerUp={() => {
                              clearTimeout(missionTimerRef.current);
                              if (isMissionRevealed) {
                                // Keep the words visible briefly so people can read.
                                missionAutoHideRef.current = setTimeout(() => {
                                  setIsPeekingWords(false);
                                  setIsMissionRevealed(false);
                                }, 2500);
                              } else {
                                setIsPeekingWords(false);
                                setIsMissionRevealed(false);
                              }
                            }}
                            onPointerCancel={() => {
                              clearTimeout(missionTimerRef.current);
                              setIsPeekingWords(false);
                              setIsMissionRevealed(false);
                            }}
                            onTouchEnd={() => {
                              clearTimeout(missionTimerRef.current);
                              if (isMissionRevealed) {
                                missionAutoHideRef.current = setTimeout(() => {
                                  setIsPeekingWords(false);
                                  setIsMissionRevealed(false);
                                }, 2500);
                              } else {
                                setIsPeekingWords(false);
                                setIsMissionRevealed(false);
                              }
                            }}
                            onTouchCancel={() => {
                              clearTimeout(missionTimerRef.current);
                              setIsPeekingWords(false);
                              setIsMissionRevealed(false);
                            }}
                            onPointerLeave={() => {
                              clearTimeout(missionTimerRef.current);
                              if (isMissionRevealed) {
                                missionAutoHideRef.current = setTimeout(() => {
                                  setIsPeekingWords(false);
                                  setIsMissionRevealed(false);
                                }, 2500);
                              } else {
                                setIsPeekingWords(false);
                                setIsMissionRevealed(false);
                              }
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                          >
                            {isPeekingWords && !isMissionRevealed ? (
                              <div className="relative z-10 flex items-center justify-center">
                                <svg key={`mission-ring-${missionHoldNonce}`} className="w-14 h-14 reveal-ring" viewBox="0 0 64 64">
                                  <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                  <circle
                                    cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="6"
                                    strokeDasharray="176" strokeDashoffset="176"
                                    className="mission-ring-circle"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <Fingerprint
                                className={`w-12 h-12 relative z-10 transition-all duration-200 ${isPeekingWords ? "text-white/70 scale-125" : "text-white/30 scale-100"}`}
                              />
                            )}
                          </Button>
                        </div>
                      );
                    })()
                  )}

                  {/* DIRECTOR BADGE IN HEADER */}
                  {room.settings?.director_mode && room.players.find(p => p.role === "director") && (
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-full px-4 py-2 flex items-center gap-2">
                      <Crown className="w-3 h-3 text-purple-400" />
                      <span className="text-[10px] font-black text-white tracking-widest uppercase">
                        DIRECTOR: <span className="text-purple-400">{room.players.find(p => p.role === "director")?.name}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>


          <div className="flex flex-col gap-2 px-4 sm:px-10 max-w-xl mx-auto">
            {room.players.filter(p => p.is_connected).map((player, index) => {
              const isDirector = player.role === "director";
              const isStarter = player.id === room.starting_player_id;
              const isRevealing = revealingPlayerId === player.id;
              const isDeleteRevealed = deleteRevealPlayerId === player.id;

              return (
                <div
                  key={player.id}
                  className={`glass flex items-center gap-0 p-0 rounded-xl transition-all relative overflow-hidden h-14
                        ${!player.is_alive ? 'opacity-40 grayscale pointer-events-none' : ''} 
                        ${isStarter ? 'border border-[hsl(var(--cyber-yellow))] shadow-[0_0_15px_rgba(255,220,90,0.1)]' : 'border border-white/5'}
                        ${isDirector ? 'border border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.1)]' : ''}
                      `}
                >
                  {/* Progress Bar Background (neutral, no cyan) */}
                  {isRevealing && !isWordRevealed && (
                    <div key={`reveal-fill-${player.id}-${revealHoldNonce}`} className="absolute inset-0 bg-white/5 z-0">
                      <div className="h-full bg-white/10 progress-fill-reveal" />
                    </div>
                  )}
                  {/* Hidden Delete Button (revealed by swipe) */}
                  {player.is_alive && (
                    <div
                      data-delete-reveal="true"
                      className={`absolute inset-y-0 left-0 w-12 z-30 transition-all duration-150
                        ${isDeleteRevealed ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-2 pointer-events-none"}`}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-full w-12 hover:bg-red-500/10 hover:text-red-400 transition-colors border-r border-white/5 rounded-none"
                        onClick={() => {
                          if (confirm(`¿Eliminar a ${player.name} de la partida?`)) {
                            handleKickPlayer(player.id);
                          }
                          setDeleteRevealPlayerId(null);
                        }}
                        aria-hidden={!isDeleteRevealed}
                        tabIndex={isDeleteRevealed ? 0 : -1}
                      >
                        <Trash2 className="w-4 h-4 opacity-40 hover:opacity-100" />
                      </Button>
                    </div>
                  )}

                  {/* Player Content Area (swipe right to reveal delete) */}
                  <div
                    className={`flex-1 h-full flex flex-col justify-center items-center px-4 min-w-0 relative z-10 text-center transition-transform duration-150 ${isDeleteRevealed ? "translate-x-12" : "translate-x-0"}`}
                    style={{ touchAction: "pan-y" }}
                    onPointerDown={(e) => {
                      if (!player.is_alive) return;
                      // Close any previously revealed delete when starting a new gesture.
                      if (deleteRevealPlayerId && deleteRevealPlayerId !== player.id) {
                        setDeleteRevealPlayerId(null);
                      }
                      deleteSwipeStartXRef.current = e.clientX;
                      deleteSwipeActiveIdRef.current = player.id;
                      // Keep receiving move/up even if finger leaves the element.
                      try {
                        e.currentTarget.setPointerCapture(e.pointerId);
                      } catch {
                        // ignore
                      }
                    }}
                    onPointerMove={(e) => {
                      if (deleteSwipeActiveIdRef.current !== player.id) return;
                      const dx = e.clientX - deleteSwipeStartXRef.current;
                      // Swipe right to reveal. Swipe left to hide.
                      if (dx > 28) setDeleteRevealPlayerId(player.id);
                      if (dx < -28) setDeleteRevealPlayerId(null);
                    }}
                    onPointerUp={() => {
                      if (deleteSwipeActiveIdRef.current === player.id) deleteSwipeActiveIdRef.current = null;
                    }}
                    onPointerCancel={() => {
                      if (deleteSwipeActiveIdRef.current === player.id) deleteSwipeActiveIdRef.current = null;
                    }}
                    onTouchStart={(e) => {
                      if (!player.is_alive) return;
                      const t = e.touches?.[0];
                      if (!t) return;
                      if (deleteRevealPlayerId && deleteRevealPlayerId !== player.id) {
                        setDeleteRevealPlayerId(null);
                      }
                      deleteSwipeStartXRef.current = t.clientX;
                      deleteSwipeTouchActiveIdRef.current = player.id;
                    }}
                    onTouchMove={(e) => {
                      if (deleteSwipeTouchActiveIdRef.current !== player.id) return;
                      const t = e.touches?.[0];
                      if (!t) return;
                      const dx = t.clientX - deleteSwipeStartXRef.current;
                      if (dx > 28) setDeleteRevealPlayerId(player.id);
                      if (dx < -28) setDeleteRevealPlayerId(null);
                    }}
                    onTouchEnd={() => {
                      if (deleteSwipeTouchActiveIdRef.current === player.id) deleteSwipeTouchActiveIdRef.current = null;
                    }}
                    onTouchCancel={() => {
                      if (deleteSwipeTouchActiveIdRef.current === player.id) deleteSwipeTouchActiveIdRef.current = null;
                    }}
                  >
                    {isRevealing && isWordRevealed ? (
                      <span className={`font-display font-black tracking-tighter text-lg sm:text-xl animate-in zoom-in-95 duration-200 truncate ${player.waiting_next_round ? 'text-xs text-red-500 uppercase' : 'text-[hsl(var(--electric-cyan))]'}`}>
                        {player.waiting_next_round
                          ? "ENTRA PRÓXIMA RONDA"
                          : room.current_round === 0
                            ? "ESPERANDO..."
                            : (player.secret_word || "???").toUpperCase()}
                      </span>
                    ) : (
                      <div className="flex flex-col items-center gap-0 w-full truncate">
                        <div className="flex items-center gap-2">
                          {isStarter && player.is_alive && !isDirector && (
                            <div className="w-2 h-2 rounded-full bg-[hsl(var(--cyber-yellow))] animate-pulse shrink-0" title="EMPIEZA" />
                          )}
                          {isDirector && player.is_alive && (
                            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse shrink-0" title="DIRECTOR" />
                          )}
                          <span className={`font-display text-base font-black tracking-wide truncate ${player.is_alive ? 'text-white' : 'text-white/20 line-through'}`}>
                            {player.name.toUpperCase()}
                          </span>
                          {room.settings?.enable_scoring !== false && player.points > 0 && (
                            <span className="text-[10px] bg-[hsl(var(--cyber-yellow))]/20 text-[hsl(var(--cyber-yellow))] px-1.5 py-0.5 rounded font-bold shrink-0">
                              {player.points}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {player.is_alive && !player.waiting_next_round && !isRevealing && (
                      <span className="text-[7px] text-[hsl(var(--text-muted))] uppercase font-bold tracking-widest opacity-60">
                        Mantén pulsado el botón de huella para ver tu palabra
                      </span>
                    )}
                  </div>

                  {/* Dedicated Reveal Button (right) */}
                  {player.is_alive && (
                    <Button
                      variant="ghost"
                      className="h-full w-20 shrink-0 rounded-none border-l border-white/5 radial-hold-container p-0 flex items-center justify-center transition-all active:scale-95 z-20 touch-none focus:ring-0 focus-visible:ring-0 outline-none hover:bg-transparent active:bg-transparent"
                      style={{ WebkitTouchCallout: "none" }}
                      onPointerDownCapture={() => {
                        // Don't keep delete action open while revealing.
                        if (deleteRevealPlayerId) setDeleteRevealPlayerId(null);
                      }}
                      onPointerDown={(e) => {
                        if (!player.is_alive) return;
                        // New hold attempt: cancel any previous attempt and restart from 0.
                        endRevealHold();
                        revealHoldSessionRef.current += 1;
                        const session = revealHoldSessionRef.current;
                        setRevealHoldNonce(n => n + 1);

                        // If a touch sequence is active, ignore pointer duplicates.
                        if (activeRevealPointerRef.current?.type === "touch") return;

                        // Ensure we always receive the corresponding pointerup/cancel (mobile friendly).
                        try {
                          e.currentTarget.setPointerCapture(e.pointerId);
                          activeRevealPointerRef.current = { type: "pointer", id: e.pointerId };
                        } catch {
                          // Some browsers can throw if capture isn't allowed; the global fallback handles it.
                        }
                        revealStartPosRef.current = { x: e.clientX, y: e.clientY };
                        // Update ref immediately (state update can lag on mobile)
                        revealingPlayerIdRef.current = player.id;
                        setRevealingPlayerId(player.id);
                        setIsWordRevealed(false);
                        revealTimerRef.current = setTimeout(() => {
                          if (revealHoldSessionRef.current !== session) return;
                          if (revealingPlayerIdRef.current !== player.id) return;
                          setIsWordRevealed(true);
                          setSeenWordsThisRound(prev => {
                            const next = new Set(prev);
                            next.add(player.id);
                            return next;
                          });
                        }, 1000);
                      }}
                      onTouchStart={(e) => {
                        if (!player.is_alive) return;
                        // iOS Safari long-press can show a callout and swallow pointerup; touch events are more reliable.
                        e.preventDefault();

                        endRevealHold();
                        revealHoldSessionRef.current += 1;
                        const session = revealHoldSessionRef.current;
                        setRevealHoldNonce(n => n + 1);

                        activeRevealPointerRef.current = { type: "touch" };
                        const t = e.touches?.[0];
                        revealStartPosRef.current = { x: t?.clientX ?? 0, y: t?.clientY ?? 0 };
                        revealingPlayerIdRef.current = player.id;
                        setRevealingPlayerId(player.id);
                        setIsWordRevealed(false);
                        revealTimerRef.current = setTimeout(() => {
                          if (revealHoldSessionRef.current !== session) return;
                          if (revealingPlayerIdRef.current !== player.id) return;
                          setIsWordRevealed(true);
                          setSeenWordsThisRound(prev => {
                            const next = new Set(prev);
                            next.add(player.id);
                            return next;
                          });
                        }, 1000);
                      }}
                      onLostPointerCapture={() => {
                        // iOS/Safari can drop capture; make sure we reset.
                        endRevealHold();
                      }}
                      onPointerUp={(e) => {
                        try {
                          // If we captured, release to avoid getting stuck.
                          // (Release can throw if capture wasn't set.)
                          e.currentTarget.releasePointerCapture(e.pointerId);
                        } catch {
                          // ignore
                        }
                        endRevealHold();
                      }}
                      onPointerCancel={() => {
                        endRevealHold();
                      }}
                      // Fallback for browsers where Pointer Events are flaky.
                      onTouchEnd={() => endRevealHold()}
                      onTouchCancel={() => endRevealHold()}
                      onPointerLeave={() => {
                        endRevealHold();
                      }}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      {isRevealing && !isWordRevealed ? (
                        <div className="relative z-10 flex items-center justify-center">
                          <svg key={`reveal-ring-${player.id}-${revealHoldNonce}`} className="w-14 h-14 reveal-ring" viewBox="0 0 64 64">
                            <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                            <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="6" strokeDasharray="176" strokeDashoffset="176" className="progress-ring-circle" strokeLinecap="round" />
                          </svg>
                        </div>
                      ) : (
                        <Fingerprint 
                          className={`w-12 h-12 relative z-10 transition-all duration-200 
                            ${isRevealing 
                              ? (isWordRevealed ? 'text-white scale-110' : 'text-white/70 scale-125') 
                              : 'text-white/30 scale-100'}`} 
                        />
                      )}
                    </Button>
                  )}
                </div>
              );
            })}

            {/* Add Player Card */}
            {/* Add Player Row */}
            <div
              className="h-12 rounded-xl border border-dashed border-[hsl(var(--electric-cyan))]/20 hover:border-[hsl(var(--electric-cyan))]/50 hover:bg-[hsl(var(--electric-cyan))]/5 cursor-pointer flex items-center justify-center gap-2 transition-all group mt-2"
              onClick={() => {
                fetchRoster();
                setShowAddPlayerModal(true);
              }}
            >
              <Plus className="w-4 h-4 text-[hsl(var(--electric-cyan))]" />
              <span className="font-heading text-xs text-[hsl(var(--electric-cyan))] tracking-wider">AÑADIR JUGADOR</span>
            </div>
          </div>

          <div className="fixed bottom-0 left-0 right-0 p-4 bg-black/80 backdrop-blur-sm border-t border-[hsl(var(--smoke))] flex gap-3 justify-center items-center z-40">
            {/* Main Action */}
            {canStartNextRound && (
              <Button
                onClick={() => {
                  if (room.settings?.enable_scoring === false) {
                    handleNextRound();
                  } else {
                    setShowScoreModal(true);
                  }
                }}
                className="flex-1 max-w-sm bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-black text-lg h-14 rounded-xl shadow-[0_4px_20px_rgba(255,220,90,0.3)]"
              >
                {room.settings?.game_mode === "infinite"
                  ? "SIGUIENTE RONDA"
                  : `RONDA SIGUIENTE`
                }
              </Button>
            )}



            {/* Secondary Action: Abort/Exit */}
            <Button
              variant="outline"
              className="h-14 w-14 border-[hsl(var(--smoke))] hover:bg-red-900/20 hover:border-red-500/50 text-[hsl(var(--text-muted))] hover:text-red-400 rounded-xl"
              onClick={() => {
                if (confirm("¿Terminar la partida y volver al inicio?")) {
                  handleDeleteRoom(); // If host
                }
              }}
            >
              <LogOut className="w-6 h-6" />
            </Button>
          </div>

          <div className="h-20" /> {/* Spacer for fixed footer */}

          {/* Score Allocation Modal */}
          <ScoreSelectionModal
            key={room.current_round}
            open={showScoreModal}
            onOpenChange={setShowScoreModal}
            players={room.players.filter(p => !p.waiting_next_round)}
            onConfirm={async (selectedIds) => {
              try {
                if (selectedIds.length > 0) {
                  await axios.post(`${API}/rooms/${roomCode}/update-scores`, { player_ids: selectedIds });
                  toast.success("Puntos asignados");
                }
                handleNextRound();
                setShowScoreModal(false);
              } catch (error) {
                toast.error("Error al asignar puntos");
              }
            }}
          />

        </div>
      ) : room.status === "playing" && (
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div
                className="glass rounded-lg p-8 md:p-12 space-y-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
                data-testid="role-card"
                style={{
                  borderColor: 'hsl(var(--smoke))',
                  borderWidth: '2px',
                  boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="text-center space-y-8">
                  <div className="text-xs font-heading text-[hsl(var(--text-muted))] uppercase tracking-[0.2em]">
                    {currentPlayer.name}
                  </div>

                  <div className="space-y-3">
                    <div className="text-sm font-heading text-[hsl(var(--text-muted))] uppercase tracking-wider">
                      TU PALABRA SECRETA
                    </div>
                    <div
                      className="font-display text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-none transition-all duration-200 cursor-pointer select-none"
                      style={{
                        color: 'hsl(var(--text-primary))',
                        filter: `drop-shadow(0 0 10px hsl(var(--text-primary)))`
                      }}
                      data-testid="secret-word"
                      onMouseDown={() => setIsWordVisible(true)}
                      onMouseUp={() => setIsWordVisible(false)}
                      onMouseLeave={() => setIsWordVisible(false)}
                      onTouchStart={() => setIsWordVisible(true)}
                      onTouchEnd={() => setIsWordVisible(false)}
                    >
                      {isWordVisible
                        ? (currentPlayer.role === 'impostor' ? '[SIN PALABRA]' : currentPlayer.secret_word)
                        : '********'}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-[hsl(var(--smoke))]">
                    <p className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed">
                      {currentPlayer.secret_word
                        ? "Usa tu palabra secreta para identificar a tus aliados. Ten cuidado con los infiltrados."
                        : "Escucha con atención. Tu misión es descubrir cuál es la palabra secreta."}
                    </p>
                  </div>
                </div>
              </div>

              {room.settings.vote_type === "manual" && currentPlayer.is_alive && (
                <div className="mt-6">
                  <Button
                    data-testid="manual-expulsion-button"
                    onClick={handleManualExpulsion}
                    className="w-full bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90 font-heading font-semibold py-6"
                  >
                    HE SIDO ELIMINADO
                  </Button>
                  <p className="text-center text-sm text-[hsl(var(--text-muted))] mt-2">
                    Presiona cuando seas eliminado de ronda manualmente
                  </p>
                </div>
              )}

              {!currentPlayer.is_alive && (
                <div className="mt-6 space-y-4">
                  <Button
                    data-testid="show-pattern-button"
                    onClick={handleShowPattern}
                    className="w-full bg-[hsl(var(--electric-cyan))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--electric-cyan))]/90 font-heading font-semibold py-6"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    VER CARTA PATRÓN
                  </Button>
                  <p className="text-center text-sm text-[hsl(var(--text-muted))]">
                    Has sido eliminado de ronda. Puedes ver la carta patrón con todas las palabras.
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="glass rounded-lg p-3 sm:p-4 space-y-2" data-testid="players-list">
                <h3 className="font-heading text-lg font-semibold mb-2">JUGADORES</h3>

                <div className="space-y-1.5">
                  {room.players.map((player, index) => (
                    <div
                      key={player.id}
                      data-testid={`game-player-${index}`}
                      className={`p-2 rounded-lg border transition-all duration-200 ${player.waiting_next_round
                        ? 'bg-slate-800/50 border-slate-700/60'
                        : player.is_alive
                          ? 'bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))] hover:border-white/20'
                          : 'bg-[hsl(var(--charcoal))]/30 border-[hsl(var(--smoke))]/20 opacity-50'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-heading text-sm font-semibold ${player.is_alive ? '' : 'line-through'}`}>
                            {player.name}
                          </span>
                          <div className="flex items-center gap-2">
                            {!player.is_connected && !room.settings?.is_local && <Badge variant="outline" className="text-xs border-amber-500 text-amber-500">OFFLINE</Badge>}
                            {player.role === "director" && (
                              <Badge className="bg-[hsl(var(--hot-pink))] text-white border-none font-bold animate-pulse">
                                DIRECTOR
                              </Badge>
                            )}
                            {player.id === room.host_id && !room.settings?.is_local && (
                              <div className="flex items-center gap-2 text-[hsl(var(--cyber-yellow))]">
                                <Crown className="w-5 h-5" />
                                <span className="text-sm font-heading">HOST</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {room.settings?.enable_scoring !== false && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 h-5 bg-[hsl(var(--cyber-yellow))]/10 text-[hsl(var(--cyber-yellow))] border border-[hsl(var(--cyber-yellow))]/20">
                              {player.points} pts
                            </Badge>
                          )}
                          {room.settings.reveal_roles && !player.is_alive && player.role && (
                            <Badge
                              className="text-[10px] px-1.5 h-5"
                              style={{
                                backgroundColor: `${getRoleColor(player.role)}20`,
                                color: getRoleColor(player.role),
                                borderColor: `${getRoleColor(player.role)}40`
                              }}
                            >
                              {getRoleLabel(player.role)}
                            </Badge>
                          )}

                          {player.waiting_next_round && (
                            <Badge variant="outline" className="text-[10px] px-1 border-cyan-500 text-cyan-500">
                              ESPERA
                            </Badge>
                          )}
                          {player.is_alive && player.id === room.starting_player_id && (
                            <Badge className="bg-[hsl(var(--cyber-yellow))] text-black text-[9px] font-black h-4 px-1 animate-pulse">
                              <Sparkles className="w-2 h-2 mr-0.5" /> EMPIEZA
                            </Badge>
                          )}
                          {!player.is_connected && (
                            <Badge variant="outline" className="text-[10px] px-1 border-amber-500 text-amber-500">
                              OFF
                            </Badge>
                          )}
                          {!player.is_alive && !player.waiting_next_round && (
                            <X className="w-3 h-3 text-[hsl(var(--hot-pink))]" />
                          )}
                        </div>
                      </div>

                      {room.settings.vote_type !== "manual" && player.is_alive && !player.waiting_next_round && currentPlayer.is_alive && player.id !== playerId && (
                        <div className="flex gap-1.5 w-full">
                          <Button
                            data-testid={`vote-button-${index}`}
                            onClick={() => handleVote(player.id)}
                            disabled={hasVoted}
                            size="sm"
                            className={`flex-1 font-heading text-[10px] h-7 ${currentPlayer.voted_for === player.id && !hasVoted
                              ? 'bg-[hsl(var(--laser-green))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--laser-green))]/90'
                              : 'bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90'
                              }`}
                          >
                            {hasVoted ? "VOTADO" : currentPlayer.voted_for === player.id ? "SELECCIONADO" : "VOTAR ELIMINAR"}
                          </Button>
                          {currentPlayer.is_host && (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 border-red-500/50 text-red-500 hover:bg-red-500 hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`¿ELIMINAR DE RONDA a ${player.name}?`)) {
                                  axios.post(`${API}/rooms/${roomCode}/manual-expulsion`, { player_id: player.id })
                                    .then(() => toast.success(`${player.name} eliminado de ronda`))
                                    .catch(() => toast.error("Error al eliminar de ronda"));
                                }
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                      {room.settings.vote_type === "manual" && !room.settings.is_local && player.is_alive && currentPlayer.is_host && player.id !== playerId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full font-heading text-[9px] h-7 border border-red-500/30 text-red-400 hover:bg-red-500/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`¿Marcar como ELIMINADO DE RONDA a ${player.name}?`)) {
                              axios.post(`${API}/rooms/${roomCode}/manual-expulsion`, { player_id: player.id })
                                .then(() => toast.success(`${player.name} eliminado de ronda`))
                                .catch(() => toast.error("Error al eliminar de ronda"));
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> ELIMINAR DE RONDA (HOST)
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-[hsl(var(--smoke))] text-center text-sm text-[hsl(var(--text-muted))]">
                  {(() => {
                    const expelledImpostor = room.players.find(p => p.role === "impostor" && !p.is_alive);
                    const impostorDeciding = expelledImpostor && room.status === "playing";

                    if (impostorDeciding && currentPlayer.id !== expelledImpostor.id) {
                      return "Esperando decisión del impostor...";
                    }

                    if (hasVoted) {
                      return "Esperando a otros jugadores...";
                    }

                    if (!currentPlayer.is_alive) {
                      return "Observando la partida...";
                    }

                    if (room.settings.vote_type === "manual") {
                      return "Votación manual - Anuncia cuando seas eliminado de ronda";
                    }

                    return "Vota para eliminar de ronda a un jugador";
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
      }

      <Dialog open={showPattern} onOpenChange={setShowPattern}>
        <DialogContent className="glass border-[hsl(var(--smoke))] max-w-md" data-testid="pattern-card-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Palabras de la Ronda</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              Estas eran las palabras secretas de la ronda actual.
            </DialogDescription>
          </DialogHeader>

          {patternData && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-6 rounded-lg text-center bg-[hsl(var(--cyber-yellow))]/10 border-2 border-[hsl(var(--cyber-yellow))] shadow-[0_0_20px_rgba(255,220,90,0.4)]">
                  <p className="text-sm uppercase font-heading text-[hsl(var(--cyber-yellow))]">Palabra Agente</p>
                  <p className="text-3xl font-bold font-display text-white mt-2">{patternData.agent_word}</p>
                </div>
                <div className="p-6 rounded-lg text-center bg-[hsl(var(--hot-pink))]/10 border-2 border-[hsl(var(--hot-pink))] shadow-[0_0_20px_rgba(255,105,180,0.4)]">
                  <p className="text-sm uppercase font-heading text-[hsl(var(--hot-pink))]">Palabra Infiltrado</p>
                  <p className="text-3xl font-bold font-display text-white mt-2">{patternData.infiltrator_word}</p>
                </div>
              </div>

              {showImpostorChoice && (
                <div className="space-y-4 pt-4 border-t border-[hsl(var(--smoke))]">
                  <p className="text-center font-heading text-sm text-[hsl(var(--text-secondary))]">
                    Como impostor eliminado de ronda, ¿sabías cuál era la palabra correcta?
                  </p>
                  <div className="flex gap-4">
                    <Button
                      data-testid="impostor-knew-button"
                      onClick={() => handleImpostorGuess(true)}
                      disabled={loading}
                      className="flex-1 bg-[hsl(var(--laser-green))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--laser-green))]/90 font-heading font-semibold disabled:opacity-50"
                    >
                      <Check className="w-5 h-5 mr-2" />
                      {loading ? "..." : "SÍ SABÍA"}
                    </Button>
                    <Button
                      data-testid="impostor-didnt-know-button"
                      onClick={() => handleImpostorGuess(false)}
                      disabled={loading}
                      className="flex-1 bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90 font-heading font-semibold disabled:opacity-50"
                    >
                      <X className="w-5 h-5 mr-2" />
                      {loading ? "..." : "NO SABÍA"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showVoteConfirm} onOpenChange={setShowVoteConfirm}>
        <DialogContent className="glass border-[hsl(var(--smoke))]" data-testid="vote-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Confirmar Voto</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              ¿Estás seguro de tu voto?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-center font-heading text-lg">
              Vas a votar por: <span className="text-[hsl(var(--hot-pink))]">
                {room?.players.find(p => p.id === selectedVote)?.name}
              </span>
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowVoteConfirm(false)}
                className="flex-1 bg-[hsl(var(--charcoal))] hover:bg-[hsl(var(--smoke))] font-heading"
                variant="outline"
              >
                CANCELAR
              </Button>
              <Button
                onClick={() => handleVote(selectedVote, true)}
                className="flex-1 bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90 font-heading font-semibold"
                data-testid="confirm-vote-button"
              >
                CONFIRMAR VOTO
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVoteSummary} onOpenChange={setShowVoteSummary}>
        <DialogContent className="glass border-[hsl(var(--smoke))] max-w-2xl" data-testid="vote-summary-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Resumen de Votación</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              Resultados de la votación
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[hsl(var(--charcoal))] rounded-lg p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[hsl(var(--smoke))]">
                    <th className="text-left py-2 font-heading text-sm">Votante</th>
                    <th className="text-left py-2 font-heading text-sm">Votó a</th>
                  </tr>
                </thead>
                <tbody>
                  {voteSummary.map((vote, index) => (
                    <tr key={index} className="border-b border-[hsl(var(--smoke))]/30">
                      <td className="py-2 text-sm">{vote.voter}</td>
                      <td className="py-2 text-sm text-[hsl(var(--hot-pink))]">{vote.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={() => setShowVoteSummary(false)}
              className="w-full bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold"
            >
              CONTINUAR
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showEndGameConfirm} onOpenChange={setShowEndGameConfirm}>
        <DialogContent className="glass border-[hsl(var(--smoke))]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Finalizar Partida</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-secondary))]">
              ¿Estás seguro de que quieres finalizar la partida para todos los jugadores? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowEndGameConfirm(false)}
            >
              Cancelar
            </Button>
            <Button
              className="bg-[hsl(var(--hot-pink))] text-white hover:bg-[hsl(var(--hot-pink))]/90"
              onClick={handleDeleteRoom}
              data-testid="confirm-end-game-button"
            >
              Finalizar Partida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AddLocalPlayerModal
        open={showAddPlayerModal}
        onOpenChange={setShowAddPlayerModal}
        roster={roster}
        currentPlayers={room.players.map(p => p.name)}
        selectedNames={selectedRosterNames}
        setSelectedNames={setSelectedRosterNames}
        newName={newRosterName}
        setNewName={setNewRosterName}
        onAddNameToRoster={handleAddNameToRoster}
        onConfirm={handleConfirmAddPlayers}
        loading={loading}
      />





      {/* --- Director Mode Modal --- */}
      <Dialog
        open={showDirectorModal}
        onOpenChange={(open) => {
          // Prevent closing if we are in Round 0 (setup phase) to force selection
          if (!open && room.current_round === 0) return;
          setShowDirectorModal(open);
        }}
      >

        <DialogContent className="bg-[hsl(var(--charcoal))] border-[hsl(var(--electric-cyan))] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[hsl(var(--electric-cyan))] text-center">
              {directorPhase === "select" ? "SELECCIONAR DIRECTOR" : "PALABRAS SECRETAS"}
            </DialogTitle>
            <DialogDescription className="text-center text-[hsl(var(--text-muted))]">
              {directorPhase === "select"
                ? "Elige quién propondrá las palabras en esta ronda (no jugará)."
                : `Pasa el dispositivo a ${room?.players?.find(p => p.id === directorPlayerId)?.name}`}
            </DialogDescription>
          </DialogHeader>

          {directorPhase === "select" ? (
            <div className="grid grid-cols-2 gap-3 py-4">
              {room?.players?.map(p => (
                <Button
                  key={p.id}
                  variant="outline"
                  className={`h-12 border-[hsl(var(--smoke))] hover:bg-[hsl(var(--electric-cyan))]/10 hover:border-[hsl(var(--electric-cyan))] hover:text-[hsl(var(--electric-cyan))] transition-all ${directorPlayerId === p.id ? 'bg-[hsl(var(--electric-cyan))]/20 border-[hsl(var(--electric-cyan))] text-[hsl(var(--electric-cyan))]' : ''}`}
                  onClick={() => setDirectorPlayerId(p.id)}
                >
                  <span className="truncate w-full text-left font-heading">{p.name}</span>
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label className="text-[hsl(var(--cyber-yellow))] font-heading">Palabra de AGENTES</Label>
                <Input
                  value={directorWords.agent}
                  onChange={(e) => setDirectorWords({ ...directorWords, agent: e.target.value })}
                  placeholder="Ej: Playa"
                  className="bg-black/30 border-[hsl(var(--cyber-yellow))]/30 text-lg font-bold text-center"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[hsl(var(--hot-pink))] font-heading">Palabra de INFILTRADO</Label>
                <Input
                  value={directorWords.infiltrator}
                  onChange={(e) => setDirectorWords({ ...directorWords, infiltrator: e.target.value })}
                  placeholder="Ej: Piscina"
                  className="bg-black/30 border-[hsl(var(--hot-pink))]/30 text-lg font-bold text-center"
                />
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-center">
            {directorPhase === "select" ? (
              <Button
                onClick={() => setDirectorPhase("input")}
                disabled={!directorPlayerId}
                className="w-full bg-[hsl(var(--electric-cyan))] text-black font-bold"
              >
                CONFIRMAR DIRECTOR
              </Button>
            ) : (
              <Button
                onClick={handleDirectorStartRound}
                disabled={!directorWords.agent || !directorWords.infiltrator}
                className="w-full bg-[hsl(var(--cyber-yellow))] text-black font-bold"
              >
                INICIAR RONDA
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddLocalPlayerModal({
  open, onOpenChange, roster, currentPlayers,
  selectedNames, setSelectedNames, newName, setNewName,
  onAddNameToRoster, onConfirm, loading
}) {
  const toggleSelection = (name) => {
    setSelectedNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  // Filtrar jugadores que YA están en la partida
  const availableRoster = roster.filter(name => !currentPlayers.includes(name));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[hsl(var(--electric-cyan))]/50 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[hsl(var(--cyber-yellow))] flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> AÑADIR JUGADORES
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--text-muted))]">
            Añade jugadores nuevos o selecciona de tu lista guardada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Input para nuevo jugador */}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Nuevo Jugador</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Nombre del jugador..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAddNameToRoster()}
                className="bg-black/40 border-[hsl(var(--smoke))]/30 h-10"
              />
              <Button
                onClick={onAddNameToRoster}
                className="bg-[hsl(var(--electric-cyan))] text-black hover:bg-[hsl(var(--electric-cyan))]/90"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Roster persistente */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-[10px] uppercase tracking-widest text-[hsl(var(--hot-pink))]">
                ROSTER ({selectedNames.length} SELECCIONADOS)
              </Label>
              <span className="text-[10px] text-[hsl(var(--text-muted))]">Disponibles: {availableRoster.length}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 max-h-[30vh] overflow-y-auto pr-1">
              {availableRoster.length === 0 ? (
                <div className="col-span-full py-6 text-center text-xs text-[hsl(var(--text-muted))] italic border border-dashed border-white/10 rounded-lg">
                  No hay más jugadores guardados.
                </div>
              ) : (
                availableRoster.map((name) => (
                  <div
                    key={name}
                    className={`flex items-center gap-2 p-2 rounded-lg border transition-all cursor-pointer ${selectedNames.includes(name)
                      ? "bg-[hsl(var(--electric-cyan))]/10 border-[hsl(var(--electric-cyan))]"
                      : "bg-black/20 border-white/5 hover:bg-white/5"
                      }`}
                    onClick={() => toggleSelection(name)}
                  >
                    <div className={`w-4 h-4 rounded flex items-center justify-center border ${selectedNames.includes(name) ? "bg-[hsl(var(--electric-cyan))] border-[hsl(var(--electric-cyan))]" : "border-white/20"
                      }`}>
                      {selectedNames.includes(name) && <Check className="w-3 h-3 text-black" />}
                    </div>
                    <span className="text-xs font-medium truncate">{name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={onConfirm}
            disabled={selectedNames.length === 0 || loading}
            className="w-full bg-[hsl(var(--cyber-yellow))] text-black font-bold h-12 shadow-[0_4px_15px_rgba(255,220,90,0.2)]"
          >
            {loading ? "PROCESANDO..." : `AÑADIR ${selectedNames.length > 0 ? selectedNames.length : ''} JUGADORES`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScoreSelectionModal({ open, onOpenChange, players, onConfirm }) {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const togglePlayer = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[hsl(var(--cyber-yellow))]" data-testid="score-modal">
        <DialogHeader>
          <DialogTitle className="text-center font-display text-2xl text-[hsl(var(--cyber-yellow))]">
            ASIGNAR PUNTOS
          </DialogTitle>
          <DialogDescription className="text-center text-[hsl(var(--text-secondary))]">
            Agente: +1 | Infiltrado: +2 | Impostor: +3
          </DialogDescription>
        </DialogHeader>

        {/* Preset Buttons */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] h-8 border-[hsl(var(--electric-cyan))]/30 text-[hsl(var(--electric-cyan))] hover:bg-[hsl(var(--electric-cyan))]/10"
            onClick={() => setSelected(players.filter(p => p.is_connected && p.role === 'agent').map(p => p.id))}
          >
            Todos Agentes (+1)
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] h-8 border-[hsl(var(--hot-pink))]/30 text-[hsl(var(--hot-pink))] hover:bg-[hsl(var(--hot-pink))]/10"
            onClick={() => setSelected(players.filter(p => p.is_connected && p.role === 'infiltrator').map(p => p.id))}
          >
            Infiltrados (+2)
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] h-8 border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
            onClick={() => setSelected(players.filter(p => p.is_connected && p.role === 'impostor').map(p => p.id))}
          >
            Solo Impostor (+3)
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] h-8 border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
            onClick={() => setSelected(players.filter(p => p.is_connected && (p.role === 'infiltrator' || p.role === 'impostor')).map(p => p.id))}
          >
            Infiltrados (+2) + Imp. (+3)
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 py-4 max-h-[60vh] overflow-y-auto">
          {players.sort((a, b) => b.is_connected - a.is_connected).map(player => (
            <div
              key={player.id}
              className={`p-3 rounded-md border transition-all flex flex-col gap-1 
                ${!player.is_connected ? 'opacity-40 cursor-not-allowed bg-black/20 border-white/5' :
                  selected.includes(player.id) ? 'bg-[hsl(var(--cyber-yellow))]/20 border-[hsl(var(--cyber-yellow))] cursor-pointer' : 'bg-black/40 border-transparent hover:bg-white/5 cursor-pointer'}`}
              onClick={() => player.is_connected && togglePlayer(player.id)}
            >
              <div className="flex items-center justify-between">
                <span className="font-heading font-bold text-sm">
                  {player.name}
                  {!player.is_connected && <span className="ml-2 text-[10px] text-red-500">(OFF)</span>}
                </span>
                {selected.includes(player.id) && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-bold text-[hsl(var(--cyber-yellow))]">
                      +{player.role?.toLowerCase() === 'agent' ? '1' : player.role?.toLowerCase() === 'infiltrator' ? '2' : '3'}
                    </span>
                    <Check className="w-4 h-4 text-[hsl(var(--cyber-yellow))]" />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-[10px] uppercase font-black tracking-tighter ${player.role === 'agent' ? 'text-[hsl(var(--electric-cyan))]' :
                  player.role === 'infiltrator' ? 'text-[hsl(var(--hot-pink))]' :
                    'text-amber-500'
                  }`}>
                  {player.role === 'agent' ? 'Agente' :
                    player.role === 'infiltrator' ? 'Infiltrado' :
                      'Impostor'}
                </span>
                <span className="text-[10px] text-[hsl(var(--text-muted))]">{player.points} pts</span>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2">
          <Button
            onClick={() => onConfirm(selected)}
            className="w-full bg-[hsl(var(--cyber-yellow))] text-black font-bold h-12"
          >
            CONFIRMAR Y SIGUIENTE RONDA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
